import { useState } from 'react';
import { TextField, Alert } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import useTriggerFamousSalesOrderFetch from 'src/api/temp/famous/useTriggerFamousSalesOrderFetch';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportFamousOrderModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [famousOrderId, setFamousOrderId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const triggerFetch = useTriggerFamousSalesOrderFetch(() => {
    onSuccess();
    handleClose();
  });

  const handleClose = () => {
    setFamousOrderId('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!famousOrderId.trim()) {
      setError('Please enter a Famous Sales Order ID');
      return;
    }

    setError(null);

    try {
      await triggerFetch.mutateAsync({
        famousSalesOrderId: famousOrderId.trim(),
      });
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to import order. Please try again.',
      );
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      open={open}
      title="Import Famous Order"
      closeable
      onCloseButton={{
        text: 'Cancel',
        onClick: handleClose,
        disabled: triggerFetch.isLoading,
      }}
      onConfirmButton={{
        text: triggerFetch.isLoading ? 'Importing...' : 'Import Order',
        onClick: handleSubmit,
        disabled: triggerFetch.isLoading || !famousOrderId.trim(),
        color: 'info',
        variant: 'contained',
      }}
      maxWidth="sm"
    >
      <MDBox p={3}>
        <MDTypography variant="body2" color="text" mb={3}>
          Enter the Famous Sales Order ID to import the order data for EDI
          processing.
        </MDTypography>

        <TextField
          fullWidth
          label="Famous Sales Order ID"
          value={famousOrderId}
          onChange={(e) => setFamousOrderId(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={triggerFetch.isLoading}
          placeholder="e.g., 12345"
          variant="outlined"
          size="medium"
          sx={{ mb: 2 }}
          autoFocus
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </MDBox>
    </Modal>
  );
};

export default ImportFamousOrderModal;
